import { useEffect, useState } from 'react'
import { ChevronLeft, ChevronRight, Loader2, Eye } from 'lucide-react'
import { useAdmin } from '../../hooks/use-admin'
import { useI18n } from '../../i18n'
import type { AdminOrderItem } from '../../types'

const payStatusMap: Record<string, { label: string; labelEn: string; cls: string }> = {
  PENDING: { label: '待支付', labelEn: 'Pending', cls: 'bg-amber-50 text-amber-600' },
  PAID: { label: '已支付', labelEn: 'Paid', cls: 'bg-emerald-50 text-emerald-600' },
  FAILED: { label: '失败', labelEn: 'Failed', cls: 'bg-red-50 text-red-500' },
  REFUNDED: { label: '已退款', labelEn: 'Refunded', cls: 'bg-gray-100 text-gray-500' },
  CLOSED: { label: '已关闭', labelEn: 'Closed', cls: 'bg-gray-100 text-gray-400' },
}

const orderTypeMap: Record<string, { label: string; labelEn: string }> = {
  NEW: { label: '新购', labelEn: 'New' },
  UPGRADE: { label: '升级', labelEn: 'Upgrade' },
  RENEW: { label: '续费', labelEn: 'Renew' },
  TOPUP: { label: '充值', labelEn: 'Topup' },
}

export function OrderSection() {
  const { orders, orderTotal, orderPage, orderTotalPages, loading, fetchOrders, fetchOrderDetail } = useAdmin()
  const { t, lang } = useI18n()

  const [detailOrder, setDetailOrder] = useState<AdminOrderItem | null>(null)

  useEffect(() => {
    fetchOrders(1)
  }, [fetchOrders])

  const handleViewDetail = async (orderNo: string) => {
    const detail = await fetchOrderDetail(orderNo)
    if (detail) setDetailOrder(detail)
  }

  const goToPage = (page: number) => {
    if (page >= 0 && page < orderTotalPages) {
      fetchOrders(page + 1)
    }
  }

  const getPayStatus = (s: string) => payStatusMap[s] || { label: s, labelEn: s, cls: 'bg-gray-50 text-gray-400' }
  const getOrderType = (t: string) => orderTypeMap[t] || { label: t, labelEn: t }

  return (
    <div className="space-y-4">
      <h2 className="text-sm font-medium text-[#1a1a1a]">
        {t('订单管理', 'Order Management')}
      </h2>

      {loading && orders.length === 0 ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="h-5 w-5 animate-spin text-orange-500" />
        </div>
      ) : orders.length === 0 ? (
        <div className="text-center py-16 text-sm text-gray-400">
          {t('暂无订单', 'No orders')}
        </div>
      ) : (
        <>
          <div className="rounded-xl border border-black/[0.06] overflow-hidden">
            <div className="grid grid-cols-[100px_1fr_70px_70px_70px_80px_50px] gap-2 px-4 py-2.5 bg-black/[0.02] text-[11px] text-gray-400 font-medium border-b border-black/[0.04]">
              <span>{t('订单号', 'Order#')}</span>
              <span>{t('企业ID', 'Enterprise')}</span>
              <span>{t('类型', 'Type')}</span>
              <span>{t('金额', 'Amount')}</span>
              <span>{t('渠道', 'Channel')}</span>
              <span>{t('状态', 'Status')}</span>
              <span></span>
            </div>
            {orders.map((order) => {
              const st = getPayStatus(order.payStatus)
              const ot = getOrderType(order.orderType)
              return (
                <div
                  key={order.id}
                  className="grid grid-cols-[100px_1fr_70px_70px_70px_80px_50px] gap-2 px-4 py-3 border-b border-black/[0.03] last:border-b-0 hover:bg-black/[0.01] transition-colors items-center text-[13px]"
                >
                  <span className="font-mono text-gray-500 truncate">{order.orderNo?.slice(-8)}</span>
                  <span className="text-[#1a1a1a] truncate text-[12px] font-mono">{order.enterpriseId || '-'}</span>
                  <span className="text-[11px] px-1.5 py-0.5 rounded-md bg-black/[0.04] text-gray-500 w-fit">
                    {lang === 'zh' ? ot.label : ot.labelEn}
                  </span>
                  <span className="text-emerald-600 font-medium">¥{order.amount}</span>
                  <span className="text-gray-400 text-[12px]">
                    {order.payChannel === 'ALIPAY' ? t('支付宝', 'Alipay') : order.payChannel === 'WECHAT' ? t('微信', 'WeChat') : order.payChannel}
                  </span>
                  <span className={`text-[11px] px-1.5 py-0.5 rounded-md w-fit ${st.cls}`}>
                    {lang === 'zh' ? st.label : st.labelEn}
                  </span>
                  <button
                    onClick={() => handleViewDetail(order.orderNo)}
                    className="p-1.5 rounded-lg text-gray-400 hover:text-orange-500 hover:bg-orange-50 transition-all"
                  >
                    <Eye className="h-3.5 w-3.5" />
                  </button>
                </div>
              )
            })}
          </div>

          {orderTotalPages > 1 && (
            <div className="flex items-center justify-between text-[12px] text-gray-400">
              <span>{t(`共 ${orderTotal} 条`, `${orderTotal} orders`)}</span>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => goToPage(orderPage - 1)}
                  disabled={orderPage <= 0}
                  className="p-1.5 rounded-lg hover:bg-black/[0.04] disabled:opacity-30 transition-all"
                >
                  <ChevronLeft className="h-4 w-4" />
                </button>
                <span className="px-2">{orderPage + 1} / {orderTotalPages}</span>
                <button
                  onClick={() => goToPage(orderPage + 1)}
                  disabled={orderPage >= orderTotalPages - 1}
                  className="p-1.5 rounded-lg hover:bg-black/[0.04] disabled:opacity-30 transition-all"
                >
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {detailOrder && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/30 backdrop-blur-sm" onClick={() => setDetailOrder(null)} />
          <div className="relative bg-white rounded-2xl border border-black/[0.06] shadow-xl w-full max-w-md mx-4 p-5 max-h-[80vh] overflow-y-auto">
            <h3 className="text-sm font-semibold text-[#1a1a1a] mb-4">
              {t('订单详情', 'Order Detail')}
            </h3>
            <div className="space-y-2.5">
              {[
                [t('订单号', 'Order#'), detailOrder.orderNo],
                [t('企业ID', 'Enterprise'), detailOrder.enterpriseId],
                [t('用户ID', 'User'), detailOrder.userId],
                [t('套餐', 'Plan'), detailOrder.planCode],
                [t('类型', 'Type'), getOrderType(detailOrder.orderType) ? (lang === 'zh' ? getOrderType(detailOrder.orderType).label : getOrderType(detailOrder.orderType).labelEn) : detailOrder.orderType],
                [t('金额', 'Amount'), `¥${detailOrder.amount}`],
                [t('渠道', 'Channel'), detailOrder.payChannel],
                [t('状态', 'Status'), getPayStatus(detailOrder.payStatus) ? (lang === 'zh' ? getPayStatus(detailOrder.payStatus).label : getPayStatus(detailOrder.payStatus).labelEn) : detailOrder.payStatus],
                [t('Token额度', 'Tokens'), detailOrder.tokenQuota?.toLocaleString()],
                [t('有效期', 'Duration'), detailOrder.durationDays ? `${detailOrder.durationDays}${t('天', 'd')}` : '-'],
                [t('创建时间', 'Created'), detailOrder.createTime],
                [t('支付时间', 'Paid At'), detailOrder.payTime || '-'],
              ].map(([label, value]) => (
                <div key={label as string} className="flex justify-between text-[13px]">
                  <span className="text-gray-400">{label}</span>
                  <span className="text-[#1a1a1a] font-medium text-right max-w-[60%] truncate">{value}</span>
                </div>
              ))}
            </div>
            <div className="mt-4 flex justify-end">
              <button
                onClick={() => setDetailOrder(null)}
                className="px-4 py-2 text-[13px] rounded-xl bg-black/[0.04] text-gray-400 hover:text-[#1a1a1a] transition-all"
              >
                {t('关闭', 'Close')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
